import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import { useApi } from "../../../services/useApi";
import { authService } from "../../../services/auth.service";
import axios from "axios";
import { ApiSuccessResponse } from "../../../types/api";
import DataTable from "../../../components/common/DataTable";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from '../../../hooks/useCommonOptions';
import SubmissionReportDialog, { SubmissionReport, BatchError } from '../components/SubmissionReportDialog';
import { useDynamicMappings } from '../../../hooks/useDynamicMappings';
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {UpdateIfExistsMap, BatchRecordError, MappingSubmitResponse} from '../../../interfaces/CommonInterface';
import { dineroFinanceColumns } from "../../../constants/DineroContants";
import { defaultPageSize, rowPerPageOptions } from "../../../constants/CommonConstants";


interface DineroEntry {
  AccountNumber: string;
  AccountName?: string;
  Date?: string;
  VoucherNumber?: string;
  VoucherType?: string;
  Description?: string;
  VatType?: string;
  VatCode?: string; // ISO date string
  Amount?: string; // ISO date string
  EntryGuid?: string; // ISO date string hoặc null nếu chưa bị xoá
  ContactGuid?: string; // UUID
  ContactName?: string;
  ContactNumber?: string;
  Type:  'Normal' | 'Primo' | 'Ultimo';
}

interface AccountingYear {
  Name: string;
  FromDate: string;
  ToDate: string;
  SalarySumTaxStateEnum: string;
}


type SupplierPaymentType = {
  "-1": "None",
  "1": "Plus71",
  "2": "Plus73",
  "5": "Plus04",
  "7": "BankTransfer",
  "10": "Iban",
  "14": "EntryOnly",
  "17": "Plus75"
}

interface EconomicEntry {
  dineroGuid?: string;
  amount: string;
  currency: string;
  date: string;
  entryTypeNumber: string;
  entryTypeNumberOriginal?: string;
  journalNumber: string;
  voucherNumber: string;
  accountNumber?: string;
  contraAccountNumber?: string;
  contraVatCode?: string;
  costTypeNumber?: string;
  customerInvoiceNumber?: string;
  customerNumber?: string;
  dueDate?: string;
  projectNumber?: string;
  supplierInvoiceNumber?: string;
  supplierNumber?: string;
  supplierPaymentBank?: string;
  supplierPaymentType?: keyof SupplierPaymentType;
  text?: string;

  vatCode?: string;
  vatCodeOriginal?: string;

  invoiceNumber?: string;
  contactNumber?: string;
}

interface FinancePanelProps {
}

// type CustomerPanelResponse =
//   | ApiSuccessResponse<{
//       Collection: DineroContact[];
//       Pagination: Pagination;
//     }>
//   | ApiErrorResponse;

interface Mapping extends EconomicEntry{}

interface DefaultValues extends Mapping {}


const FinancePanel: React.FC<FinancePanelProps> = () => {
  const defaultTemplate: Mapping = {
    dineroGuid: "",
    amount: "Amount",
    currency: "",
    date: "Date",
    entryTypeNumber: "VoucherType",
    journalNumber: "",
    voucherNumber: "VoucherNumber",
    accountNumber: "AccountNumber",

    contraAccountNumber: "",
    contraVatCode: "",
    costTypeNumber: "",
    customerInvoiceNumber: "",
    customerNumber: "",
    dueDate: "",
    projectNumber: "",
    invoiceNumber: "VoucherNumber",
    supplierNumber: "",
    supplierPaymentBank: "",
    text: "Description",
    vatCode: "VatCode",

    contactNumber: "ContactName",
  };
  const handleLoadDataDinero = (filters: {
    accountingYear: String;
    includePrimo: boolean;
  }) => {
    console.log('Loading entry data with filters:', filters);
  };

  const [accountingYears, setAccountingYears] = useState<AccountingYear[]>([]);
  const [selectedAccountingYear, setSelectedAccountingYear] = useState<string>('');
  const [accountingYearsLoading, setAccountingYearsLoading] = useState<boolean>(false);

  const [includePrimo, setIncludePrimo] = useState(false);
  const [dineroEntries, setDineroEntries] = useState<DineroEntry[]>([]);
  const [economicEntries, setEconomicEntries] = useState<EconomicEntry[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>(defaultTemplate);

  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [batchSize, setBatchSize] = useState(10);

  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    dineroGuid: "",
    amount: "",
    currency: "",
    date: "",
    entryTypeNumber: "",
    journalNumber: "",
    voucherNumber: "",
    accountNumber: "",
    contactNumber: "",
    contraAccountNumber: "",
    contraVatCode: "",
    costTypeNumber: "",
    customerInvoiceNumber: "",
    customerNumber: "",
    dueDate: "",
    projectNumber: "",
    invoiceNumber: "",
    supplierNumber: "",
    supplierPaymentBank: "",
    text: "",
    vatCode: ""
  });
  const [requiredFields] = useState<Array<keyof Mapping>>([
    'amount',
    'currency',
    'date',
    'entryTypeNumber',
    'journalNumber',
    'voucherNumber'
  ]);
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] = useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>({});

  const api = useApi();
  const { journals,entryTypeOptions,currencies,vatCodes } = useCommonOptions();
  const { getMappedValue: getDynamicMappedValue} = useDynamicMappings();

  const handleUpdateData = () => {
    mapDineroToEconomic();
  };

  useEffect(() => {
    const loadAccountingYears = async () => {
      setAccountingYearsLoading(true);
      try {
        const response = await api.get<ApiSuccessResponse<AccountingYear[]>>(
          "/dinero/accounting-years"
        );
        if (response.data.success) {
          setAccountingYears(response.data.data);
          // Set the last year as default if available
          if (response.data.data.length > 0) {
            const lastYear = response.data.data[response.data.data.length - 1];
            setSelectedAccountingYear(lastYear.Name);
          }
        }
      } catch (error) {
        console.error("Error loading accounting years:", error);
        setError("Failed to load accounting years. Please try again later.");
      } finally {
        setAccountingYearsLoading(false);
      }
    };

    loadAccountingYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - api is stable from useApi hook

  useEffect(() => {
    if (economicEntries.length > 0) {
      let newBatchSize = 10; // Default minimum

      if (economicEntries.length <= 100) {
        newBatchSize = 10;
      } else if (economicEntries.length <= 500) {
        newBatchSize = 50;
      } else if (economicEntries.length <= 1000) {
        newBatchSize = 100;
      } else {
        newBatchSize = 500; // Maximum batch size for very large datasets
      }

      setBatchSize(newBatchSize);
    }
  }, [economicEntries.length]);


  const handleLoadData = async () => {
    if (!selectedAccountingYear) {
      setError("Please select an accounting year");
      return;
    }



    const indexSelectedYear = accountingYears.findIndex(year => year.Name === selectedAccountingYear);
    const selectedYear = accountingYears[indexSelectedYear];
    const previousYear = accountingYears[indexSelectedYear - 1];
    const nextYear = accountingYears[indexSelectedYear + 1];
    if (!selectedYear) {
      setError("Selected accounting year not found");
      return;
    }

    setLoading(true);
    setError(null);
    setErrorRecords(new Set()); // Reset error records
    setShowReport(false);
    try {
      handleLoadDataDinero({
        accountingYear: selectedYear.Name,
        includePrimo: includePrimo
      });

      const dineroResponse = await api.get<ApiSuccessResponse<DineroEntry[]>>(
        "/dinero/entries",
        {
          params: {
            previouseFromDate: previousYear?.FromDate,
            previouseToDate: previousYear?.ToDate,
            nextFromDate: nextYear?.FromDate,
            nextToDate: nextYear?.ToDate,
            fromDate: selectedYear.FromDate,
            toDate: selectedYear.ToDate,
            includePrimo: includePrimo,
          }
        }
      );
      if (dineroResponse.data.success) {
        setDineroEntries(dineroResponse.data.data);
        setTotalCount(dineroResponse.data.data.length);
        // Map data from Dinero to e-conomic
        mapDineroToEconomic(dineroResponse.data.data);
      }
    } catch (error) {
      console.error("Error loading entry data:", error);
      if (!error.message.includes("No connection info")) {
        setError("Failed to load entry data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedAccountingYear || dineroEntries.length === 0) {
      setError("Please load data first before downloading");
      return;
    }

    try {
      const indexSelectedYear = accountingYears.findIndex(year => year.Name === selectedAccountingYear);
      const selectedYear = accountingYears[indexSelectedYear];
      const previousYear = accountingYears[indexSelectedYear - 1];
      const nextYear = accountingYears[indexSelectedYear + 1];

      if (!selectedYear) {
        setError("Selected accounting year not found");
        return;
      }

      setLoading(true);
      setError(null);

      // Use axios to download file with proper headers
      const token = authService.getToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const response = await axios.get(`${apiUrl}/dinero/entries-export`, {
        params: {
          previouseFromDate: previousYear?.FromDate || '',
          previouseToDate: previousYear?.ToDate || '',
          nextFromDate: nextYear?.FromDate || '',
          nextToDate: nextYear?.ToDate || '',
          fromDate: selectedYear.FromDate,
          toDate: selectedYear.ToDate,
          includePrimo: includePrimo.toString(),
        },
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `dinero_entries_${selectedYear.Name}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      setSuccess("Excel file downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading Excel:", error);
      setError("Failed to download Excel file. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapDineroToEconomic = (resDineroEntries?: DineroEntry[]) => {
    const dineroEntryData = resDineroEntries || dineroEntries;
    const mappedEntries = dineroEntryData.map((item,index) => {
      function getMappedValue(field: string): { mapped: string | null, original: string | null } {
        const def = getDefaultValue(field as keyof DefaultValues);
        if (def === "---") return { mapped: null, original: null };
        if (def) return { mapped: def, original: null };
        if (field in mapping) {
          const key = mapping[field];
          if (key && key in item) {
            const raw = item[key as keyof typeof item];
            const mapped = getDynamicMappedValue(field, raw, item);
            return { mapped, original: raw };
          }
        }

        return { mapped: null, original: null };
      }
      const { mapped: entryTypeNumber, original: entryTypeNumberOriginal } = getMappedValue("entryTypeNumber");
      const { mapped: vatCode, original: vatCodeOriginal } = getMappedValue("vatCode");
      let invoiceNumber = getMappedValue("invoiceNumber").mapped;
      if(entryTypeNumber === "3" || entryTypeNumber === "4"){
        invoiceNumber = "";
      }
      return {
        dineroGuid: item.EntryGuid,
        amount:getMappedValue("amount").mapped,
        currency:getMappedValue("currency").mapped,
        date:getMappedValue("date").mapped,
        entryTypeNumber:entryTypeNumber,
        entryTypeNumberOriginal:entryTypeNumberOriginal,
        journalNumber:getMappedValue("journalNumber").mapped,
        voucherNumber:getMappedValue("voucherNumber").mapped,
        contactNumber:getMappedValue("contactNumber").mapped,
        accountNumber:getMappedValue("accountNumber").mapped,
        contraAccountNumber:getMappedValue("contraAccountNumber").mapped,
        contraVatCode:getMappedValue("contraVatCode").mapped,
        costTypeNumber:getMappedValue("costTypeNumber").mapped,
        // customerInvoiceNumber:getMappedValue("customerInvoiceNumber").mapped,
        customerNumber:getMappedValue("customerNumber").mapped,
        dueDate:getMappedValue("dueDate").mapped,
        projectNumber:getMappedValue("projectNumber").mapped,
        invoiceNumber:invoiceNumber,
        supplierNumber:getMappedValue("supplierNumber").mapped,
        supplierPaymentBank:getMappedValue("supplierPaymentBank").mapped,
        text:getMappedValue("text").mapped,
        vatCode:vatCode,
        vatCodeOriginal:vatCodeOriginal
      };
    });
    setEconomicEntries(mappedEntries);
    setSuccess("Successfully mapped " + economicEntries.length + " entries");
  };

  const validateMapping = (): string[] => {
    const errors: string[] = [];
    const uniqueFields = new Set(requiredFields);

    uniqueFields.forEach(field => {
      const mappingValue = mapping[field];
      const defaultValue = defaultValues[field];

      if (!mappingValue && !defaultValue) {
        const fieldName = field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
        errors.push(`Field "${fieldName}" is required. Please map a field or set a default value.`);
      }
    });

    return errors;
  };

  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping();
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    // if (updateExistingProduct && !updateKey) {
    //   setError("Please select a key for updating existing products");
    //   return;
    // }

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicEntries.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        // Check if should stop
        if (shouldStop) {
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchItems = economicEntries.slice(start, end);

        const filteredBatch = batchItems
          .map(item => deepClean(item))
          .filter(item => item && Object.keys(item).length > 0);

        const response = await api.post<MappingSubmitResponse>(
          "/main/create-entry",
          {
            mapping,
            entries: filteredBatch,
            batchInfo: {
              currentBatch: i + 1,
              totalBatches: batches,
            },
            isLastBatch: i === batches - 1,
            updateIfExists: updateIfExistsMap
          },
          {
            signal: abortControllerRef.current.signal
          }
        );

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach(error => {
            setErrorRecords(prev => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach(error => {
          if (typeof error === 'string') {
            reportErrors.push(error);
          } else {
            error.errors.forEach(err => {
              reportErrors.push({
                recordId: error.recordId,
                field: '',
                message: err,
                value: ''
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error) {

      if (error.status === 500 && error.message === 'canceled') {
         setShowReport(true);
         setShouldStop(false);
        // Don't show message here as it's already shown in handleStopSubmit
      } else {
        console.error("Error submitting mapping:", error);
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
      console.log('finally');
    }
  };

  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(`Process stopped. Successfully processed ${processedCount} records.`);
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };

  const dineroColumns = dineroFinanceColumns;

  const scrollToErrorRow = (recordId: string) => {
    // Find the index of the contact with the matching recordId
    const productIndex = economicEntries.findIndex(entry => entry.dineroGuid === recordId);
    if (productIndex === -1) return;

    // Calculate which page the contact is on
    const newPage = Math.floor(productIndex / rowsPerPage);

    // If the contact is not on the current page, change the page
    if (newPage !== page) {
      setPage(newPage);
    }

    // Wait for the page to update and then scroll to the row
    setTimeout(() => {
      const row = document.getElementById(`row-${recordId}`);
      if (row && tableRef.current) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight the row temporarily
        row.style.backgroundColor = 'rgba(255, 0, 0, 0.3)';
        setTimeout(() => {
          row.style.backgroundColor = '';
        }, 2000);
      }
    }, 100); // Small delay to allow page update
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
          Filter Entries from Dinero
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Accounting Year</InputLabel>
            <Select
              value={selectedAccountingYear}
              onChange={(event) => setSelectedAccountingYear(event.target.value)}
              label="Accounting Year"
              disabled={accountingYearsLoading}
              renderValue={(value) => {
                const selectedYear = accountingYears && accountingYears.length > 0 ? accountingYears.find(year => year.Name === value) : null;
                return selectedYear ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      {selectedYear.Name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ({selectedYear.FromDate} &rarr; {selectedYear.ToDate})
                    </Typography>
                  </Box>
                ) : value;
              }}
            >
              {accountingYears && accountingYears.length > 0 ? accountingYears.map((year) => (
                <MenuItem key={year.Name} value={year.Name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'blue' }}>
                      {year.Name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      ({year.FromDate} &rarr; {year.ToDate})
                    </Typography>
                  </Box>
                </MenuItem>
              )) : null}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch
              checked={includePrimo}
              onChange={(event) => setIncludePrimo(event.target.checked)}
              size="small"
            />}
            label="Include Primo"
          />
          <Button
            variant="contained"
            onClick={handleLoadData}
            sx={{ minWidth: 120 }}
            disabled={loading || accountingYearsLoading}
          >
            {loading ? <CircularProgress size={24} /> : "Load Data"}
          </Button>
          {dineroEntries.length > 0 && (
            <Tooltip title="Download Excel">
              <IconButton
                color="primary"
                onClick={handleDownloadExcel}
                disabled={loading}
                sx={{ ml: 1 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
      <MappingDialogs
          mapping={mapping}
          defaultValues={defaultValues}
          type="finance"
          onApplyMapping={(newMap, defaults) => {
            setMapping(newMap);
            setDefaultValues(defaults);
          }}
          onLoadDefaultTemplate = {()=>{
            setMapping(defaultTemplate);
          }}
          availableColumns={dineroColumns.map(col => ({ value: col.id, label: col.label }))}
          onSuccess={(msg) => setSuccess(msg)}
          showButtonGenerateStandardInformation={true}
      />


    <SubmitOptions
        mapDineroToEconomic={handleUpdateData}
        dineroData={dineroEntries}
        economicData={economicEntries}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
      />

      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
      >
        Dinero Entries
      </Typography>

      <Box
        sx={{
          "& table": {
            tableLayout: "auto", // 👈 Cho phép tự động tính width
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            fontSize: "0.75rem",
            fontWeight: "bold",
            width: "auto", // 👈 Cho phép tự co giãn
            whiteSpace: "nowrap", // 👈 Tránh xuống dòng nếu muốn giữ nguyên 1 dòng
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
          },
        }}
      >
        <DataTable
          columns={dineroColumns}
          data={dineroEntries}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          maxHeight={600}
          size="small"
        />
      </Box>

      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt:2 }}
      >
          E-conomic Entries
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 600 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic customer table"
        >
          <TableHead>
            <TableRow>
            <MappingCell
                key="journalNumber"
                label="Journal Number"
                field="journalNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={journals}

                />
            <MappingCell
                key="Type"
                label="Entry Type"
                field="entryTypeNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={entryTypeOptions}
              />
              <MappingCell
                key="date"
                label="Date"
                field="date"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="contactNumber"
                label="Customer/Supplier"
                field="contactNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="accountNumber"
                label="Account Number"
                field="accountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="voucherNumber"
                label="Voucher Number"
                field="voucherNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) => handleUpdateIfExistsChange('voucherNumber', value)}
              />
              <MappingCell
                key="text"
                label="Text"
                field="text"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="amount"
                label="Amount"
                field="amount"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="currency"
                label="Currency"
                field="currency"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={currencies}
              />
              <MappingCell
                key="vatCode"
                label="VAT Code"
                field="vatCode"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={vatCodes}
              />
              <MappingCell
                key="contraAccountNumber"
                label="Contra Account Number"
                field="contraAccountNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                key="contraVatCode"
                label="Contra VAT Code"
                field="contraVatCode"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
                itemOptions={vatCodes}
              />
              <MappingCell
                key="invoiceNumber"
                label="Invoice Number"
                field="invoiceNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={dineroColumns}
                requiredFields={requiredFields}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicEntries
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((entry) => {
                  const hasError = errorRecords.has(entry.dineroGuid);
                  return (
                    <TableRow
                      key={entry.dineroGuid}
                      id={`row-${entry.dineroGuid}`}
                      sx={{
                        backgroundColor: hasError ? 'rgba(255, 0, 0, 0.1)' : undefined,
                        '&:hover': {
                          backgroundColor: hasError ? 'rgba(255, 0, 0, 0.2)' : undefined
                        }
                      }}
                    >
                      <ValueCell value={entry.journalNumber} options={journals}/>
                      <ValueCell value={entry.entryTypeNumber} options={entryTypeOptions} originalValue={entry.entryTypeNumberOriginal}/>
                      <ValueCell value={entry.date} />
                      <ValueCell value={entry.contactNumber} />
                      <ValueCell value={entry.accountNumber} />
                      <ValueCell value={entry.voucherNumber} />
                      <ValueCell value={entry.text} />
                      <ValueCell value={entry.amount} />
                      <ValueCell value={entry.currency} options={currencies} />
                      <ValueCell value={entry.vatCode} options={vatCodes} originalValue={entry.vatCodeOriginal} />
                      <ValueCell value={entry.contraAccountNumber} />
                      <ValueCell value={entry.contraVatCode} options={vatCodes} />
                      <ValueCell value={entry.invoiceNumber} />
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={rowPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>

      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={scrollToErrorRow}
      />
    </>
  );
};

export default memo(FinancePanel);
